import { useCallback, useEffect, useMemo, useState } from "react";

import { Loader } from "@mantine/core";
import { TFunction } from "i18next";
import { useSelector } from "react-redux";

import { Button, Modal, Select } from "components";
import { IRootState } from "config/redux";
import { ServiceConstants } from "constant";
import { UserService } from "services";
import { UserResponse } from "types";
import { AppUtils } from "utils";

import classes from "./ClientAssignAgentModal.module.css";

const { REQUEST_PENDING, REQUEST_USER_AGENT_CHANGE_ID, REQUEST_USER_AGENTS_LIST_ID } = ServiceConstants;

interface ClientAssignAgentModalProps {
  opened: boolean;
  onClose: (val: boolean) => void;
  clientId: number;
  clientName: string;
  onAssigned: () => void;
  t: TFunction;
}

const ClientAssignAgentModal = ({
  opened,
  onClose,
  clientId,
  clientName,
  onAssigned,
  t,
}: ClientAssignAgentModalProps) => {
  const requestId = REQUEST_USER_AGENT_CHANGE_ID + clientId;
  const assignServiceObject = useSelector((state: IRootState) => state.service.object[requestId]);
  const agentsServiceObject = useSelector(
    (state: IRootState) => state.service.object[REQUEST_USER_AGENTS_LIST_ID],
  );
  const [agentId, setAgentId] = useState<string | null>(null);

  const loadAgents = useCallback(() => {
    UserService.usersAgentsAll({ page: 0, search: "" });
  }, []);

  useEffect(() => {
    if (!opened) {
      setAgentId(null);
    }
  }, [opened]);

  useEffect(() => {
    if (!opened) {
      return;
    }
    AppUtils.resolveServiceObject(assignServiceObject, requestId, () => {
      onAssigned();
      onClose(false);
    });
  }, [assignServiceObject, onAssigned, onClose, opened, requestId]);

  const isLoading = useMemo(
    () => assignServiceObject && assignServiceObject.status === REQUEST_PENDING,
    [assignServiceObject],
  );
  const isAgentsLoading = useMemo(
    () => agentsServiceObject && agentsServiceObject.status === REQUEST_PENDING,
    [agentsServiceObject],
  );
  const hasAgentsResponse = useMemo(
    () => Array.isArray(agentsServiceObject?.data?.content),
    [agentsServiceObject?.data?.content],
  );

  const agentOptions = useMemo(() => {
    const content = agentsServiceObject?.data?.content as UserResponse[] | undefined;
    if (!content) {
      return [];
    }
    return content.map((agent) => ({
      value: String(agent.id),
      label: agent.fullName,
    }));
  }, [agentsServiceObject?.data?.content]);

  const handleSubmit = useCallback(() => {
    if (!agentId) {
      return;
    }
    UserService.userAgentChange(clientId, Number(agentId));
  }, [agentId, clientId]);

  return (
    <Modal
      opened={opened}
      onClose={() => onClose(false)}
      title={<div className={classes.title}>{t("clients.unassigned.modal.title")}</div>}
      size={520}
      withCloseButton={false}
    >
      <div className={classes.content}>
        <div className={classes.detailsPanel}>
          <div className={classes.field}>
            <div className={classes.summaryLabel}>{t("clients.unassigned.modal.client")}</div>
            <div className={classes.summaryValue}>{clientName}</div>
          </div>
          <div className={`${classes.field} ${classes.agentField}`}>
            <div className={classes.summaryLabel}>{t("clients.unassigned.modal.agent")}</div>
            <Select
              data={agentOptions}
              value={agentId}
              onChange={setAgentId}
              onDropdownOpen={() => {
                if (!hasAgentsResponse && !isAgentsLoading) {
                  loadAgents();
                }
              }}
              searchable={false}
              clearable={false}
              placeholder={t("clients.unassigned.modal.agentPlaceholder")}
              disabled={Boolean(isLoading)}
              rightSection={isAgentsLoading ? <Loader size={16} /> : undefined}
              nothingFoundMessage={!isAgentsLoading && hasAgentsResponse ? t("results.no") : undefined}
            />
          </div>
        </div>
        <div className={classes.confirmText}>{t("clients.unassigned.modal.description")}</div>
        <div>
          <div className={classes.actions}>
            <Button
              variant="outline"
              color="gray"
              radius="md"
              style={{ borderColor: "#ccd3e2", color: "#31384a", minWidth: 110 }}
              onClick={() => onClose(false)}
            >
              {t("action.cancel")}
            </Button>
            <Button
              color="#aeb6c6"
              disabled={!agentId}
              loading={Boolean(isLoading)}
              radius="md"
              style={{ minWidth: 150 }}
              onClick={handleSubmit}
            >
              {t("clients.unassigned.assignAgent")}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ClientAssignAgentModal;
